/**
 * @file todoViews.ts
 * @brief To-do views (F1a, DES 31-10-2026, ADR-062): List, By person, By
 *        topic, By category, By due date, By status, By meeting; the smart
 *        default and the remembered pick. Pure (plus a guarded localStorage).
 *
 * @details
 * The same rules on every app (docs/product/todo-grouping.md):
 * - Smart default, no setting: 1-4 tasks, or at most one category -> List;
 *   otherwise By category. A remembered pick wins, unless it needs labels the
 *   to-dos do not have (then the smart default).
 * - Groups: largest first, the catch-all ("Other", "No owner", "No date")
 *   last; due and status keep their fixed order; meetings newest first.
 * - Due dates: only one said in the meeting or set by a person counts (the
 *   My to-dos rule, BACKEND myTodos.mjs); a guessed one is "No date".
 *
 * @references MyTodosList.tsx, company-meetings/components/MeetingDetailView.tsx (TodosTab)
 * @author DES (prepared with Claude)
 * @date 31-10-2026
 */

export type TodoViewKey = "list" | "person" | "topic" | "category" | "due" | "status" | "meeting";
export type TodoSurface = "meeting" | "myTodos";

export const TODO_VIEW_LABELS: Record<TodoViewKey, string> = {
  list: "List",
  person: "By person",
  topic: "By topic",
  category: "By category",
  due: "By due date",
  status: "By status",
  meeting: "By meeting",
};

/** Which views each surface offers, in menu order. */
export const VIEWS_FOR: Record<TodoSurface, TodoViewKey[]> = {
  meeting: ["list", "category", "topic", "person", "due", "status"],
  myTodos: ["due", "category", "person", "meeting"],
};

/** What a view needs to know about one to-do. */
export type ViewFacts = {
  category?: string | null;
  categoryId?: string | null;
  topic?: string | null;
  person?: string | null;
  /** "YYYY-MM-DD" when the date counts (said / set), else null. */
  dueDay?: string | null;
  done?: boolean;
  meetingId?: string | null;
  meetingTitle?: string | null;
  meetingDate?: string | null;
};

export type TodoGroup<T> = {
  /** Stable key for collapse state. */
  key: string;
  label: string;
  kind: Exclude<TodoViewKey, "list">;
  items: T[];
  /** By category: the category this heading is (null for "Other"). */
  categoryId?: string | null;
  /** By topic: the topic text (null for "Other"). */
  topic?: string | null;
  tone?: "overdue";
  /** By meeting: the meeting's date. */
  meetingDate?: string | null;
};

const norm = (s: string | null | undefined) => String(s ?? "").trim();
const lowerKey = (s: string | null | undefined) => norm(s).toLowerCase().replace(/\s+/g, " ");

/** Distinct categories among the to-dos (by id, else by name). */
export function categoryCount<T>(items: T[], facts: (t: T) => ViewFacts): number {
  const set = new Set<string>();
  for (const t of items) {
    const f = facts(t);
    const k = norm(f.categoryId) || lowerKey(f.category);
    if (k) set.add(k);
  }
  return set.size;
}

/** 1-4 tasks or at most one category -> list; else by category. */
export function smartDefaultView<T>(items: T[], facts: (t: T) => ViewFacts): "list" | "category" {
  return items.length <= 4 || categoryCount(items, facts) <= 1 ? "list" : "category";
}

/** True when the view can be drawn from these to-dos (labels exist). */
export function viewHasData<T>(view: TodoViewKey, items: T[], facts: (t: T) => ViewFacts): boolean {
  if (view === "category") return items.some((t) => norm(facts(t).category));
  if (view === "topic") return items.some((t) => norm(facts(t).topic));
  return true;
}

/**
 * The view to show: the remembered pick when it fits this surface and the
 * data, else the smart default. My to-dos has no plain list: its "list" is By
 * due date (the page's existing default).
 */
export function resolveView<T>(
  surface: TodoSurface,
  remembered: TodoViewKey | null,
  items: T[],
  facts: (t: T) => ViewFacts,
): TodoViewKey {
  const allowed = VIEWS_FOR[surface];
  if (remembered && allowed.includes(remembered) && viewHasData(remembered, items, facts)) return remembered;
  const smart = smartDefaultView(items, facts);
  if (smart === "category" && allowed.includes("category")) return "category";
  return surface === "myTodos" ? "due" : "list";
}

// ── Due dates ────────────────────────────────────────────────────────────────

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const pad = (n: number) => String(n).padStart(2, "0");

/** A stored due date ("Month DD, YYYY" or ISO) -> "YYYY-MM-DD", or null. */
export function dueDayOf(raw: string | null | undefined): string | null {
  const s = norm(raw);
  if (!s) return null;
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const named = /^([A-Za-z]+)\.?\s+(\d{1,2}),?\s+(\d{4})$/.exec(s);
  if (!named) return null;
  const m = MONTHS.indexOf(named[1].toLowerCase().slice(0, 3));
  const d = Number(named[2]);
  if (m === -1 || d < 1 || d > 31) return null;
  return `${named[3]}-${pad(m + 1)}-${pad(d)}`;
}

/** A meeting to-do's due day when it counts: said in the meeting or set by a person. */
export function countedDueDay(todo: { dueDate?: string | null; dueDateSource?: string | null; source?: string | null }): string | null {
  const day = dueDayOf(todo.dueDate);
  if (!day) return null;
  const src = todo.dueDateSource ?? (todo.source === "manual" ? "user" : "guessed");
  return src === "said" || src === "user" ? day : null;
}

/** "YYYY-MM-DD" on this device's calendar. */
export function localToday(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function addDays(day: string, n: number): string {
  const [y, m, d] = day.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

export const DUE_BUCKETS = ["overdue", "today", "week", "later", "none"] as const;
export type DueBucket = (typeof DUE_BUCKETS)[number];
export const DUE_BUCKET_LABELS: Record<DueBucket, string> = {
  overdue: "Overdue",
  today: "Today",
  week: "This week",
  later: "Later",
  none: "No date",
};

/** Overdue, Today, This week (tomorrow + 6 days), Later, No date. */
export function dueBucketOf(dueDay: string | null | undefined, today: string): DueBucket {
  if (!dueDay) return "none";
  if (dueDay < today) return "overdue";
  if (dueDay === today) return "today";
  if (dueDay <= addDays(today, 6)) return "week";
  return "later";
}

// ── Grouping ─────────────────────────────────────────────────────────────────

const OTHER = "Other";

function byLabel<T>(
  items: T[],
  kind: "category" | "topic" | "person",
  pick: (f: ViewFacts) => { key: string; label: string; id?: string | null } | null,
  facts: (t: T) => ViewFacts,
  fallbackLabel: string,
): TodoGroup<T>[] {
  const groups = new Map<string, TodoGroup<T>>();
  const firstSeen: string[] = [];
  for (const t of items) {
    const p = pick(facts(t));
    const key = p?.key || `__${kind}_none`;
    let g = groups.get(key);
    if (!g) {
      g = {
        key: `${kind}:${key}`,
        label: p?.label || fallbackLabel,
        kind,
        items: [],
        ...(kind === "category" ? { categoryId: p?.id ?? null } : {}),
        ...(kind === "topic" ? { topic: p ? p.label : null } : {}),
      };
      groups.set(key, g);
      firstSeen.push(key);
    }
    g.items.push(t);
  }
  const none = `__${kind}_none`;
  return firstSeen
    .map((k, i) => ({ g: groups.get(k)!, i, none: k === none }))
    .sort((a, b) => Number(a.none) - Number(b.none) || b.g.items.length - a.g.items.length || a.i - b.i)
    .map((x) => x.g);
}

/**
 * The groups for a view (empty groups left out). `list` = one group of all.
 * @param today "YYYY-MM-DD" on the viewer's calendar (due view)
 */
export function groupTodos<T>(
  items: T[],
  view: TodoViewKey,
  facts: (t: T) => ViewFacts,
  today: string = localToday(),
): TodoGroup<T>[] {
  if (view === "list") return [{ key: "list", label: "", kind: "status", items }];
  if (view === "category") {
    return byLabel(
      items,
      "category",
      (f) => {
        const label = norm(f.category);
        return label ? { key: norm(f.categoryId) || lowerKey(label), label, id: f.categoryId ?? null } : null;
      },
      facts,
      OTHER,
    );
  }
  if (view === "topic") {
    return byLabel(items, "topic", (f) => (norm(f.topic) ? { key: lowerKey(f.topic), label: norm(f.topic) } : null), facts, OTHER);
  }
  if (view === "person") {
    return byLabel(items, "person", (f) => (norm(f.person) ? { key: lowerKey(f.person), label: norm(f.person) } : null), facts, "No owner");
  }
  if (view === "status") {
    const open = items.filter((t) => !facts(t).done);
    const done = items.filter((t) => facts(t).done);
    return [
      { key: "status:open", label: "Open", kind: "status" as const, items: open },
      { key: "status:done", label: "Done", kind: "status" as const, items: done },
    ].filter((g) => g.items.length);
  }
  if (view === "due") {
    return DUE_BUCKETS.map((b) => ({
      key: `due:${b}`,
      label: DUE_BUCKET_LABELS[b],
      kind: "due" as const,
      items: items.filter((t) => dueBucketOf(facts(t).dueDay, today) === b),
      ...(b === "overdue" ? { tone: "overdue" as const } : {}),
    })).filter((g) => g.items.length);
  }
  // meeting
  const groups = new Map<string, TodoGroup<T>>();
  for (const t of items) {
    const f = facts(t);
    const id = norm(f.meetingId) || "__none";
    let g = groups.get(id);
    if (!g) {
      g = { key: `meeting:${id}`, label: norm(f.meetingTitle) || "Untitled meeting", kind: "meeting", items: [], meetingDate: f.meetingDate ?? null };
      groups.set(id, g);
    }
    g.items.push(t);
  }
  const at = (g: TodoGroup<T>) => (g.meetingDate ? new Date(g.meetingDate).getTime() || 0 : 0);
  return [...groups.values()].sort((a, b) => at(b) - at(a));
}

// ── The remembered pick (this device) ────────────────────────────────────────

export const VIEW_STORAGE_KEY: Record<TodoSurface, string> = {
  meeting: "verblizr.todoView.meeting",
  myTodos: "verblizr.todoView.myTodos",
};

/** The pick saved on this browser, or null (private window, nothing saved). */
export function readRememberedView(surface: TodoSurface, storage?: Pick<Storage, "getItem"> | null): TodoViewKey | null {
  try {
    const s = storage ?? (typeof window !== "undefined" ? window.localStorage : null);
    const v = s?.getItem(VIEW_STORAGE_KEY[surface]) ?? null;
    if (v && (VIEWS_FOR[surface] as string[]).includes(v)) return v as TodoViewKey;
    // My to-dos before F1a saved "date" / "meeting" under its own key.
    if (surface === "myTodos") {
      const old = s?.getItem("verblizr.myTodos.grouping") ?? null;
      if (old === "meeting") return "meeting";
      if (old === "date") return "due";
    }
    return null;
  } catch {
    return null;
  }
}

export function rememberView(surface: TodoSurface, view: TodoViewKey, storage?: Pick<Storage, "setItem"> | null): void {
  try {
    const s = storage ?? (typeof window !== "undefined" ? window.localStorage : null);
    s?.setItem(VIEW_STORAGE_KEY[surface], view);
  } catch {
    // Private window: the pick lasts this visit only.
  }
}
